import { visitMapStyle } from '../visit-map-style.js';
import { bromoDistrictBoundary } from '../bromo-district-boundary.js';
import { tween } from '../motion-choreography.js';

// Resolve a design token to its computed value so map-rendered (canvas) colors
// stay in sync with the CSS custom properties in _variables.scss.
const token = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

document.addEventListener('alpine:init', () => {
  let mapsLoaded = false;
  let mapsLoadingPromise = null;

  function loadGoogleMaps() {
    if (mapsLoaded) return Promise.resolve();
    if (mapsLoadingPromise) return mapsLoadingPromise;

    mapsLoadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${window.GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.onload = () => {
        mapsLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return mapsLoadingPromise;
  }

  // Pill toggle that shows/hides a map overlay. `getLayer` is a factory (may be
  // async) returning anything with a setMap() method; it's called once on first
  // activation and the result reused, so the layer only builds when actually used.
  function createLayerToggle(label, map, getLayer) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'c-visit__map-toggle';
    // Leading "+" glyph before the label; CSS rotates it 45° into an "×" when the
    // toggle is active (BugHerd #166). aria-hidden — the label carries the meaning.
    button.innerHTML =
      '<span class="o-icon" aria-hidden="true">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M5 12h14"/><path d="M12 5v14"/></svg></span>';
    button.appendChild(document.createTextNode(label));
    button.setAttribute('aria-pressed', 'false');

    let layerPromise = null;
    button.addEventListener('click', async () => {
      const active = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('is-active', active);

      if (!layerPromise) layerPromise = Promise.resolve(getLayer());
      const layer = await layerPromise;
      // Re-read state after the await so rapid toggles settle on the latest value.
      layer.setMap(button.getAttribute('aria-pressed') === 'true' ? map : null);
    });

    return button;
  }

  // Baltimore City's own bike network, drawn as styled polylines. Google's
  // BicyclingLayer has no styling API and renders faintly over the custom map
  // style, so we render the city data ourselves. The dataset is a separate Vite
  // chunk loaded on demand (first activation) to keep the main bundle small.
  //
  // Returns a controller with a Maps-overlay-style setMap(): showing/hiding fades
  // strokeOpacity via the shared, token-timed tween (canvas overlays can't take a
  // CSS transition), so it eases in/out instead of popping. The tween resolves its
  // duration + easing from the motion tokens, so prefers-reduced-motion collapses
  // the fade to instant for free.
  async function buildBikeLayer() {
    const { bromoBikeRoutes } = await import('../bromo-bike-routes.js');
    const data = new google.maps.Data();
    data.addGeoJson(bromoBikeRoutes);

    const color = token('--c-teal-300') || '#00C1AE';
    let opacity = 0;
    const draw = () =>
      data.setStyle({ strokeColor: color, strokeWeight: 3, strokeOpacity: opacity, zIndex: 2 });
    draw();

    let cancel = null;
    const fadeTo = (target, fragment, onDone) => {
      if (cancel) cancel();
      const from = opacity;
      cancel = tween(
        fragment,
        (eased) => {
          opacity = from + (target - from) * eased;
          draw();
        },
        () => {
          cancel = null;
          onDone && onDone();
        },
      );
    };

    return {
      setMap(map) {
        if (map) {
          data.setMap(map); // attach before fading in
          fadeTo(1, 'medium entrance');
        } else {
          fadeTo(0, 'short exit', () => data.setMap(null)); // detach after fading out
        }
      },
    };
  }

  Alpine.data('googleMap', () => ({
    async init() {
      const el = this.$refs.map;
      const lat = parseFloat(el.dataset.lat);
      const lng = parseFloat(el.dataset.lng);
      const zoom = parseInt(el.dataset.zoom, 10) || 15;

      if (!lat || !lng) return;

      await loadGoogleMaps();

      const map = new google.maps.Map(el, {
        center: { lat, lng },
        zoom,
        styles: visitMapStyle,
        mapTypeControl: false, // replaced by the Transit / Bike route toggles below
      });

      new google.maps.Marker({
        position: { lat, lng },
        map,
      });

      // Outline the Bromo Tower Arts & Entertainment District boundary.
      const districtAccent = token('--c-coral-400') || '#FF6946';
      map.data.addGeoJson(bromoDistrictBoundary);
      map.data.setStyle({
        strokeColor: districtAccent,
        strokeWeight: 3,
        strokeOpacity: 0.9,
        fillColor: districtAccent,
        fillOpacity: 0.1,
        clickable: false,
      });

      // Custom top-left controls replacing the default Map / Satellite toggle.
      const controls = document.createElement('div');
      controls.className = 'c-visit__map-controls';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Map route layers');
      controls.append(
        createLayerToggle('Transit Routes', map, () => new google.maps.TransitLayer()),
        createLayerToggle('Bike Routes', map, buildBikeLayer),
      );

      map.controls[google.maps.ControlPosition.TOP_LEFT].push(controls);
    },
  }));
});
